import {
    Card,
    Colors,
    Heading,
    Icon,
    PageContainer,
    Text,
    type DraggableItem,
} from '@amelio/ui-library';
import { useEffect, useState, type Ref } from 'react';

import { EvaluatorCountInput } from './EvaluatorCountInput/EvaluatorCountInput';
import { InfoCallout } from './InfoCallout/InfoCallout';
import { PrioritySection } from './PrioritySection/PrioritySection';
import { SelectEvaluatorsHeader } from './SelectEvaluatorsHeader/SelectEvaluatorsHeader';

import type { PriorityAttribute } from '@/types/priorityAttributeType';

import { FeedbackCommentRepliesCard } from '@/components/FeedbackCommentRepliesCard/FeedbackCommentRepliesCard';
import { FeedbackPrivacyCard } from '@/components/FeedbackPrivacyCard/FeedbackPrivacyCard';
import { SelectEvaluatorsArrowIllustration } from '@/components/Illustrations/Illustration';
import {
    Feedback360SettingsLock,
    NEW_FEEDBACK_360_COMMENT_REPLIES_ENABLED,
    type Feedback360SettingsLockValue,
} from '@/constants/review/Feedback360Settings';
import { FeedbackPrivacy, type FeedbackPrivacyValue } from '@/constants/review/FeedbackPrivacy';
import { useAmelioTranslations } from '@/hooks';
import { StepFooter } from '@/navigation/shared/StepFooter';
import {
    CYCLE_CARD_SURFACE,
    CYCLE_CARD_SURFACE_BG_COLOR,
    CYCLE_CARD_SURFACE_BORDER_COLOR,
} from '@/pages/CycleEvaluation/components/cycleCardSurface';

interface SelectEvaluatorsProps {
    dataTestId?: string;
    isReadOnly?: boolean;
    evaluatorsCount?: number;
    priority1?: DraggableItem<PriorityAttribute>[];
    priority2?: DraggableItem<PriorityAttribute>[];
    priority3?: DraggableItem<PriorityAttribute>[];
    feedbackPrivacy?: FeedbackPrivacyValue;
    commentRepliesEnabled?: boolean;
    feedback360SettingsLock?: Feedback360SettingsLockValue;
    blockingMessages?: string[];
    /** The anonymous minimum the number of reviewers falls short of, said under that number. */
    countErrorMessage?: string;
    countInputRef?: Ref<HTMLInputElement>;
    onFeedbackPrivacyChange?: (feedbackPrivacy: FeedbackPrivacyValue) => void;
    onCommentRepliesEnabledChange?: (commentRepliesEnabled: boolean) => void;
    onEvaluatorsCountChange?: (count: number) => void;
    onPriority1Change?: (items: DraggableItem<PriorityAttribute>[]) => void;
    onPriority2Change?: (items: DraggableItem<PriorityAttribute>[]) => void;
    onPriority3Change?: (items: DraggableItem<PriorityAttribute>[]) => void;
    onAddAttribute?: (priority: 1 | 2 | 3) => void;
    onRemoveAttribute?: (priority: 1 | 2 | 3, itemId: string) => void;
    showIntroHeader?: boolean;
}

const SelectEvaluators = ({
    dataTestId = 'select-evaluators',
    isReadOnly = false,
    evaluatorsCount: initialCount = 5,
    priority1: initialPriority1,
    priority2: initialPriority2,
    priority3: initialPriority3,
    feedbackPrivacy = FeedbackPrivacy.NAMED,
    commentRepliesEnabled = NEW_FEEDBACK_360_COMMENT_REPLIES_ENABLED,
    feedback360SettingsLock,
    blockingMessages,
    countErrorMessage,
    countInputRef,
    onFeedbackPrivacyChange,
    onCommentRepliesEnabledChange,
    onPriority1Change,
    onPriority2Change,
    onPriority3Change,
    onAddAttribute,
    onRemoveAttribute,
    onEvaluatorsCountChange,
    showIntroHeader = true,
}: SelectEvaluatorsProps) => {
    const { t } = useAmelioTranslations();

    const [evaluatorsCount, setEvaluatorsCount] = useState(initialCount);
    const [priority1, setPriority1] = useState(initialPriority1 || []);
    const [priority2, setPriority2] = useState(initialPriority2 || []);
    const [priority3, setPriority3] = useState(initialPriority3 || []);

    useEffect(() => {
        setPriority1(initialPriority1 || []);
    }, [initialPriority1]);

    useEffect(() => {
        setPriority2(initialPriority2 || []);
    }, [initialPriority2]);

    useEffect(() => {
        setPriority3(initialPriority3 || []);
    }, [initialPriority3]);

    const handleEvaluatorsCountChange = (count: number) => {
        setEvaluatorsCount(count);
        onEvaluatorsCountChange?.(count);
    };

    const handlePriority1Change = (items: DraggableItem<PriorityAttribute>[]) => {
        setPriority1(items);
        onPriority1Change?.(items);
    };

    const handlePriority2Change = (items: DraggableItem<PriorityAttribute>[]) => {
        setPriority2(items);
        onPriority2Change?.(items);
    };

    const handlePriority3Change = (items: DraggableItem<PriorityAttribute>[]) => {
        setPriority3(items);
        onPriority3Change?.(items);
    };

    const handleAddAttribute = (priority: 1 | 2 | 3) => {
        onAddAttribute?.(priority);
    };

    const handleRemoveAttribute = (priority: 1 | 2 | 3, itemId: string) => {
        onRemoveAttribute?.(priority, itemId);
    };

    return (
        <PageContainer
            className={'cycle-evaluation-group-section'}
            dataTestId={dataTestId}
        >
            {showIntroHeader && (
                <>
                    <SelectEvaluatorsHeader />

                    <SelectEvaluatorsArrowIllustration
                        className={'relative z-10 hidden sm:-top-20 sm:left-115 sm:-mb-28 sm:block'}
                    />
                </>
            )}

            <Card
                padding={'none'}
                bgColor={CYCLE_CARD_SURFACE_BG_COLOR}
                borderColor={CYCLE_CARD_SURFACE_BORDER_COLOR}
                className={[
                    CYCLE_CARD_SURFACE,
                    // The 40px inset is a pinned design-system token (never meant to shrink with
                    // root font size — see CYCLE_LAYOUT.cardPadding), not something this one page
                    // should repurpose; this override only narrows it for small viewports.
                    'max-md:p-4',
                    showIntroHeader ? 'mt-20 sm:mt-0' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <Heading level={4}>
                    {t('cycleEvaluationSelectEvaluators.evaluatorSelection.title')}
                </Heading>

                <Text className={'mt-3'}>
                    {t('cycleEvaluationSelectEvaluators.evaluatorSelection.description')}
                </Text>

                <EvaluatorCountInput
                    value={evaluatorsCount}
                    errorMessage={countErrorMessage}
                    inputRef={countInputRef}
                    isReadOnly={isReadOnly}
                    onChange={handleEvaluatorsCountChange}
                />

                <PrioritySection
                    priority1={priority1}
                    priority2={priority2}
                    priority3={priority3}
                    isReadOnly={isReadOnly}
                    onPriority1Change={handlePriority1Change}
                    onPriority2Change={handlePriority2Change}
                    onPriority3Change={handlePriority3Change}
                    onAddAttribute={handleAddAttribute}
                    onRemoveAttribute={onRemoveAttribute ? handleRemoveAttribute : undefined}
                />

                {blockingMessages && blockingMessages.length > 0 && (
                    <div
                        className={'mt-4 flex flex-col gap-2'}
                        role={'alert'}
                        data-testid={'select-evaluators-error'}
                    >
                        {blockingMessages.map((message) => (
                            <div
                                key={message}
                                className={'flex items-center gap-2'}
                            >
                                <Icon
                                    name={'essential-danger'}
                                    color={Colors.ERROR}
                                />

                                <Text
                                    size={'small'}
                                    color={Colors.ERROR}
                                >
                                    {message}
                                </Text>
                            </div>
                        ))}
                    </div>
                )}

                <InfoCallout />

                <FeedbackPrivacyCard
                    className={'mt-9'}
                    value={feedbackPrivacy}
                    lock={feedback360SettingsLock}
                    onChange={onFeedbackPrivacyChange}
                />

                <FeedbackCommentRepliesCard
                    className={'mt-6'}
                    value={commentRepliesEnabled}
                    lock={isReadOnly ? Feedback360SettingsLock.COMPLETED : undefined}
                    onChange={onCommentRepliesEnabledChange}
                />

                <StepFooter />
            </Card>
        </PageContainer>
    );
};

export { SelectEvaluators };
export type { SelectEvaluatorsProps };
